import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  Card,
  CardContent,
  Tabs,
  Tab,
  LinearProgress,
  Stack,
  InputAdornment,
  TableSortLabel,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  CalendarMonth as CalendarIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  BarChart as BarChartIcon,
  Download as DownloadIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp,
  TrendingDown,
  Category,
  DateRange,
} from '@mui/icons-material';
import {
  format,
  startOfMonth,
  endOfMonth,
  subMonths,
  subDays,
  addMonths,
  isAfter,
  parseISO,
  isValid,
} from 'date-fns';
import { saveAs } from 'file-saver';
import { api } from '@/services/api';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectBankAccount } from '@/store/slices/bankAccountSlice';
import type { Extrato, Lancamento } from '@/types/bank';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const errMsg = (e: any, fallback: string) =>
  e?.response?.data?.error?.message || e?.error?.message || e?.message || fallback;

const isoDay = (d: Date) => format(d, 'yyyy-MM-dd');

/** Interpreta a data do lançamento: 'YYYY-MM-DD' vira data local (sem fuso); ISO completo vira Date normal */
function parseData(value: string | undefined | null): Date | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = parseISO(value);
  if (isValid(d)) return d;
  const d2 = new Date(value);
  return isNaN(d2.getTime()) ? null : d2;
}

function formatDataLancamento(value: string | undefined | null): string {
  const d = parseData(value);
  if (!d) return value || '-';
  const soData = /^\d{4}-\d{2}-\d{2}$/.test(value || '');
  return soData ? format(d, 'dd/MM/yyyy') : format(d, 'dd/MM/yyyy HH:mm');
}

const formatCurrency = (value: number | null | undefined) => {
  if (value === undefined || value === null || isNaN(value)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

// Heurísticas herdadas da versão Sicoob — usadas só quando o backend não manda contraparte/categoria
function extrairNome(descricao: string, complementar?: string): string {
  const texto = complementar || descricao || '';
  // Padrão: "Recebimento Pix|@NOME|@..." ou "REM.: NOME|@..."
  const matchPix = texto.match(/(?:Recebimento Pix|Pagamento Pix|REM\.:?|FAV\.:?)\s*\|?@?\s*([^|@]+)/i);
  if (matchPix) return matchPix[1].trim();
  // Padrão: "SIPAG_Ant._Mastercard" etc
  const matchSipag = texto.match(/SIPAG[_\s]+(.*)/i);
  if (matchSipag) return `SIPAG ${matchSipag[1].trim()}`;
  // Se nenhum padrão, usar descrição curta
  return descricao?.split('|')[0]?.trim() || 'Outros';
}

function classificarTransacao(descricao: string): string {
  const d = (descricao || '').toUpperCase();
  if (d.includes('PIX RECEBIDO') || d.includes('TRANSF.RECEBIDA - PIX')) return 'PIX Recebido';
  if (d.includes('PIX EMITIDO') || d.includes('TRANSF.REALIZADA PIX')) return 'PIX Enviado';
  if (d.includes('DEP.DINHEIRO')) return 'Depósito';
  if (d.includes('DÉB.TIT.COMPE') || d.includes('DÉB. PAGAMENTO')) return 'Boleto Pago';
  if (d.includes('CR ANTECIPAÇÃO') || d.includes('CR COMPRAS') || d.includes('SIPAG')) return 'Cartão/Maquininha';
  if (d.includes('TARIFA') || d.includes('DÉBITO PACOTE')) return 'Tarifa Bancária';
  if (d.includes('SEGURO')) return 'Seguro';
  if (d.includes('CRED.TRANSF.CONTAS') || d.includes('CRÉD.TRANSF.POUPANÇA')) return 'Transferência Sicoob';
  if (d.includes('CONV.DEMAIS')) return 'Convênio';
  return 'Outros';
}

const nomeDe = (l: Lancamento) => (l.contraparteNome || '').trim() || extrairNome(l.descricao);
const categoriaDe = (l: Lancamento) => (l.categoria || '').trim() || classificarTransacao(l.descricao);
const isCredit = (l: Lancamento) => l.tipo === 'C';

const csvEscape = (v: string | number | null | undefined) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

type Atalho = 'esteMes' | 'mesPassado' | 'ultimos30' | 'ultimos90';

function periodoAtalho(atalho: Atalho): { inicio: string; fim: string } {
  const hoje = new Date();
  switch (atalho) {
    case 'esteMes':
      return { inicio: isoDay(startOfMonth(hoje)), fim: isoDay(hoje) };
    case 'mesPassado': {
      const m = subMonths(hoje, 1);
      return { inicio: isoDay(startOfMonth(m)), fim: isoDay(endOfMonth(m)) };
    }
    case 'ultimos30':
      return { inicio: isoDay(subDays(hoje, 29)), fim: isoDay(hoje) };
    case 'ultimos90':
      return { inicio: isoDay(subDays(hoje, 89)), fim: isoDay(hoje) };
  }
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export default function ContaExtratoPage() {
  const theme = useTheme();
  const account = useAppSelector(selectBankAccount);

  const [loading, setLoading] = useState(false);
  const [extrato, setExtrato] = useState<Extrato | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0); // 0 = extrato, 1 = BI
  const [searchTerm, setSearchTerm] = useState('');
  const [progress, setProgress] = useState(0);

  // Período por datas
  const [{ inicio, fim }, setPeriodo] = useState(() => periodoAtalho('esteMes'));
  const [atalhoAtivo, setAtalhoAtivo] = useState<Atalho | null>('esteMes');

  // Modal de detalhes por contraparte
  const [detailModal, setDetailModal] = useState<{
    open: boolean;
    nome: string;
    tipo: 'credito' | 'debito';
  }>({ open: false, nome: '', tipo: 'credito' });
  const [detailSort, setDetailSort] = useState<{ field: string; dir: 'asc' | 'desc' }>({ field: 'data', dir: 'desc' });

  // Validação do período
  const periodoErro = useMemo(() => {
    const di = parseData(inicio);
    const df = parseData(fim);
    if (!di || !df) return 'Informe as duas datas.';
    if (isAfter(di, df)) return 'A data inicial deve ser anterior à final.';
    if (isAfter(df, addMonths(di, 12))) return 'Período máximo de 12 meses. Reduza o intervalo.';
    return null;
  }, [inicio, fim]);

  const mesesNoPeriodo = useMemo(() => {
    const di = parseData(inicio);
    const df = parseData(fim);
    if (!di || !df) return 1;
    return (df.getFullYear() - di.getFullYear()) * 12 + (df.getMonth() - di.getMonth()) + 1;
  }, [inicio, fim]);

  const fetchExtrato = useCallback(async () => {
    if (periodoErro) {
      setError(periodoErro);
      return;
    }
    setLoading(true);
    setError(null);
    setProgress(5);

    try {
      const response = await api.get<Extrato>('/bank/extrato', {
        params: { inicio, fim },
        // Sicoob busca mês a mês com pausa entre chamadas
        timeout: mesesNoPeriodo * 5000 + 20000,
      });

      if (response.success && response.data) {
        setExtrato(response.data);
      } else {
        setError(response.error?.message || 'Erro ao carregar extrato');
      }
    } catch (err: any) {
      setError(errMsg(err, 'Erro ao carregar extrato'));
    } finally {
      setLoading(false);
      setProgress(100);
    }
  }, [inicio, fim, periodoErro, mesesNoPeriodo]);

  // Carrega ao abrir e quando a conta selecionada muda
  useEffect(() => {
    setExtrato(null);
    fetchExtrato();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id]);

  // Progresso simulado enquanto carrega
  useEffect(() => {
    if (!loading) return;
    const interval = setInterval(() => {
      setProgress(prev => Math.min(90, prev + (90 - prev) * 0.05));
    }, 500);
    return () => clearInterval(interval);
  }, [loading]);

  const aplicarAtalho = (a: Atalho) => {
    setPeriodo(periodoAtalho(a));
    setAtalhoAtivo(a);
  };

  const lancamentos = extrato?.lancamentos ?? [];

  // Filtro textual local
  const filteredLancamentos = useMemo(() => {
    if (!searchTerm.trim()) return lancamentos;
    const term = searchTerm.toLowerCase();
    return lancamentos.filter(l =>
      (l.descricao || '').toLowerCase().includes(term) ||
      (l.contraparteNome || '').toLowerCase().includes(term) ||
      (l.contraparteDocumento || '').toLowerCase().includes(term) ||
      (l.categoria || '').toLowerCase().includes(term) ||
      (l.e2eId || '').toLowerCase().includes(term) ||
      (l.txid || '').toLowerCase().includes(term) ||
      String(l.valor ?? '').includes(term)
    );
  }, [lancamentos, searchTerm]);

  // Totais (o backend manda; recalcula como fallback)
  const totais = useMemo(() => {
    let creditos = 0, debitos = 0;
    lancamentos.forEach(l => {
      const v = Number(l.valor) || 0;
      if (isCredit(l)) creditos += v;
      else debitos += v;
    });
    const c = extrato?.totalCreditos ?? creditos;
    const d = extrato?.totalDebitos ?? debitos;
    return { creditos: c, debitos: d, saldo: c - d };
  }, [extrato, lancamentos]);

  const temSaldoApos = useMemo(
    () => lancamentos.some(l => l.saldoApos !== null && l.saldoApos !== undefined),
    [lancamentos]
  );

  // ========== BI DATA ==========
  const biData = useMemo(() => {
    if (!lancamentos.length) return null;

    const porNome: Record<string, { total: number; count: number; tipo: string }> = {};
    const porCategoria: Record<string, { creditos: number; debitos: number; count: number }> = {};
    const porMes: Record<string, { creditos: number; debitos: number; count: number }> = {};
    const porDiaSemana: Record<string, { creditos: number; debitos: number; count: number }> = {};
    const diasSemana = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

    lancamentos.forEach(l => {
      const valor = Number(l.valor) || 0;
      const credit = isCredit(l);
      const nome = nomeDe(l);
      const categoria = categoriaDe(l);
      const data = parseData(l.data);

      // Por nome (separado por tipo, como na versão anterior)
      const nomeKey = `${credit ? 'C' : 'D'}|${nome.substring(0, 50)}`;
      if (!porNome[nomeKey]) porNome[nomeKey] = { total: 0, count: 0, tipo: credit ? 'credito' : 'debito' };
      porNome[nomeKey].total += valor;
      porNome[nomeKey].count++;

      if (!porCategoria[categoria]) porCategoria[categoria] = { creditos: 0, debitos: 0, count: 0 };
      if (credit) porCategoria[categoria].creditos += valor;
      else porCategoria[categoria].debitos += valor;
      porCategoria[categoria].count++;

      if (data) {
        const mesKey = `${String(data.getMonth() + 1).padStart(2, '0')}/${data.getFullYear()}`;
        if (!porMes[mesKey]) porMes[mesKey] = { creditos: 0, debitos: 0, count: 0 };
        if (credit) porMes[mesKey].creditos += valor;
        else porMes[mesKey].debitos += valor;
        porMes[mesKey].count++;

        const dia = diasSemana[data.getDay()];
        if (!porDiaSemana[dia]) porDiaSemana[dia] = { creditos: 0, debitos: 0, count: 0 };
        if (credit) porDiaSemana[dia].creditos += valor;
        else porDiaSemana[dia].debitos += valor;
        porDiaSemana[dia].count++;
      }
    });

    const semPrefixo = (k: string) => k.substring(2);

    const topCreditos = Object.entries(porNome)
      .filter(([, v]) => v.tipo === 'credito')
      .sort(([, a], [, b]) => b.total - a.total)
      .slice(0, 20)
      .map(([k, v]) => [semPrefixo(k), v] as const);

    const topDebitos = Object.entries(porNome)
      .filter(([, v]) => v.tipo === 'debito')
      .sort(([, a], [, b]) => b.total - a.total)
      .slice(0, 20)
      .map(([k, v]) => [semPrefixo(k), v] as const);

    const categoriasOrdenadas = Object.entries(porCategoria)
      .sort(([, a], [, b]) => (b.creditos + b.debitos) - (a.creditos + a.debitos));

    const mesesOrdenados = Object.entries(porMes)
      .sort(([a], [b]) => {
        const [mA, yA] = a.split('/').map(Number);
        const [mB, yB] = b.split('/').map(Number);
        return (yA * 12 + mA) - (yB * 12 + mB);
      });

    const diaSemanaOrdenado = diasSemana
      .filter(d => porDiaSemana[d])
      .map(d => ({ dia: d, ...porDiaSemana[d] }));

    return { topCreditos, topDebitos, categoriasOrdenadas, mesesOrdenados, diaSemanaOrdenado };
  }, [lancamentos]);

  // Lançamentos do modal de detalhe
  const detailLancamentos = useMemo(() => {
    if (!detailModal.open) return [];
    const nome = detailModal.nome;
    const filtered = lancamentos.filter(l => {
      const credit = isCredit(l);
      return nomeDe(l).substring(0, 50) === nome &&
        (detailModal.tipo === 'credito' ? credit : !credit);
    });

    const { field, dir } = detailSort;
    filtered.sort((a, b) => {
      let cmp = 0;
      if (field === 'data') {
        cmp = (parseData(a.data)?.getTime() || 0) - (parseData(b.data)?.getTime() || 0);
      } else if (field === 'valor') {
        cmp = (Number(a.valor) || 0) - (Number(b.valor) || 0);
      } else if (field === 'descricao') {
        cmp = (a.descricao || '').localeCompare(b.descricao || '');
      }
      return dir === 'asc' ? cmp : -cmp;
    });
    return filtered;
  }, [detailModal, lancamentos, detailSort]);

  const detailTotais = useMemo(() => {
    let total = 0;
    detailLancamentos.forEach(l => { total += Number(l.valor) || 0; });
    return { total, count: detailLancamentos.length };
  }, [detailLancamentos]);

  const handleDetailSort = (field: string) => {
    setDetailSort(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  const openDetailModal = (nome: string, tipo: 'credito' | 'debito') => {
    setDetailModal({ open: true, nome, tipo });
    setDetailSort({ field: 'data', dir: 'desc' });
  };

  // Exportar CSV do que está na tela (respeita a busca)
  const exportarCsv = () => {
    if (!filteredLancamentos.length) return;
    const header = ['Data', 'Descrição', 'Contraparte', 'Documento', 'Categoria', 'Tipo', 'Valor', 'Saldo após', 'E2E', 'txid'];
    const linhas = filteredLancamentos.map(l => [
      formatDataLancamento(l.data),
      l.descricao,
      nomeDe(l),
      l.contraparteDocumento || '',
      categoriaDe(l),
      isCredit(l) ? 'Crédito' : 'Débito',
      (isCredit(l) ? 1 : -1) * (Number(l.valor) || 0),
      l.saldoApos ?? '',
      l.e2eId || '',
      l.txid || '',
    ].map(csvEscape).join(';'));
    const csv = '\uFEFF' + [header.join(';'), ...linhas].join('\r\n');
    const nome = `extrato_${account ? `${account.provider}_${account.accountNumber || account.label}` : 'conta'}_${inicio}_${fim}.csv`
      .replace(/[^\w.\-]+/g, '_');
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), nome);
  };

  const periodoLabel = extrato
    ? `${formatDataLancamento(extrato.periodo?.inicio || inicio)} a ${formatDataLancamento(extrato.periodo?.fim || fim)}`
    : `${formatDataLancamento(inicio)} a ${formatDataLancamento(fim)}`;

  const renderLinhaLancamento = (l: Lancamento, i: number, comSaldo: boolean) => (
    <TableRow key={l.id || i} hover>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Typography variant="body2">{formatDataLancamento(l.data)}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={500}>{l.descricao || '-'}</Typography>
        {(l.e2eId || l.txid) && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace' }}>
            {l.e2eId || l.txid}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2">{l.contraparteNome || '-'}</Typography>
        {l.contraparteDocumento && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace' }}>
            {l.contraparteDocumento}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Chip size="small" variant="outlined" label={categoriaDe(l)} />
      </TableCell>
      <TableCell align="center">
        {isCredit(l) ? (
          <Chip size="small" label="Crédito" color="success" variant="outlined" icon={<ArrowDownwardIcon />} />
        ) : (
          <Chip size="small" label="Débito" color="error" variant="outlined" icon={<ArrowUpwardIcon />} />
        )}
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        <Typography variant="body2" fontWeight={600} color={isCredit(l) ? 'success.main' : 'error.main'}>
          {isCredit(l) ? '+' : '-'} {formatCurrency(Number(l.valor))}
        </Typography>
      </TableCell>
      {comSaldo && (
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {l.saldoApos === null || l.saldoApos === undefined ? (
            <Typography variant="body2" color="text.disabled">—</Typography>
          ) : (
            <Typography variant="body2" fontWeight={500} color={l.saldoApos >= 0 ? 'text.primary' : 'error.main'}>
              {formatCurrency(l.saldoApos)}
            </Typography>
          )}
        </TableCell>
      )}
    </TableRow>
  );

  const colCount = temSaldoApos ? 7 : 6;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Extrato da Conta</Typography>
          <Typography variant="body2" color="text.secondary">
            Consulte movimentações por período — até 12 meses
          </Typography>
          {account && (
            <Chip
              size="small"
              variant="outlined"
              icon={<AccountBalanceIcon sx={{ fontSize: 16 }} />}
              label={`${account.providerLabel} · ${account.label}${account.companyName ? ` — ${account.companyName}` : ''}`}
              sx={{ mt: 1 }}
            />
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Exportar CSV (linhas exibidas)">
            <span>
              <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
                onClick={exportarCsv} disabled={!filteredLancamentos.length}>
                CSV
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Atualizar">
            <span>
              <IconButton onClick={fetchExtrato} disabled={loading}><RefreshIcon /></IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {!account && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Nenhuma conta bancária selecionada. Escolha uma conta no seletor do topo da página
          (Empresa · Banco). Sem seleção, o sistema usa a conta padrão da empresa.
        </Alert>
      )}

      {error && <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      {!!extrato?.avisos?.length && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {extrato.avisos.length === 1 ? (
            extrato.avisos[0]
          ) : (
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {extrato.avisos.map((a, i) => <li key={i}>{a}</li>)}
            </Box>
          )}
        </Alert>
      )}

      {/* Filtros de Período */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Período de Consulta
        </Typography>
        <Grid container spacing={2} sx={{ mt: 0.5 }} alignItems="center">
          <Grid item xs={6} sm={3} md={2}>
            <TextField fullWidth size="small" type="date" label="Início" value={inicio}
              InputLabelProps={{ shrink: true }} inputProps={{ max: fim || undefined }}
              onChange={(e) => { setPeriodo(p => ({ ...p, inicio: e.target.value })); setAtalhoAtivo(null); }} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField fullWidth size="small" type="date" label="Fim" value={fim}
              InputLabelProps={{ shrink: true }} inputProps={{ min: inicio || undefined, max: isoDay(new Date()) }}
              onChange={(e) => { setPeriodo(p => ({ ...p, fim: e.target.value })); setAtalhoAtivo(null); }} />
          </Grid>
          <Grid item xs={12} sm={6} md={5}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {([
                ['esteMes', 'Este mês'],
                ['mesPassado', 'Mês passado'],
                ['ultimos30', 'Últimos 30 dias'],
                ['ultimos90', 'Últimos 90 dias'],
              ] as [Atalho, string][]).map(([k, label]) => (
                <Chip key={k} label={label} size="small" clickable
                  color={atalhoAtivo === k ? 'primary' : 'default'}
                  variant={atalhoAtivo === k ? 'filled' : 'outlined'}
                  onClick={() => aplicarAtalho(k)} />
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button fullWidth variant="contained" onClick={fetchExtrato} disabled={loading || !!periodoErro}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FilterIcon />}
              sx={{ height: '40px' }}>
              {loading ? 'Consultando...' : 'Consultar'}
            </Button>
          </Grid>
          {periodoErro && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ py: 0 }}>{periodoErro}</Alert>
            </Grid>
          )}
        </Grid>
        {loading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {account?.provider === 'sicoob'
                ? `Buscando extrato mês a mês no Sicoob (${mesesNoPeriodo} ${mesesNoPeriodo === 1 ? 'mês' : 'meses'}, ~${Math.ceil(mesesNoPeriodo * 1.5)}s)...`
                : 'Buscando lançamentos do período...'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Resumo */}
      {extrato && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={2.4}>
            <Card variant="outlined"><CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary">Saldo Inicial</Typography>
              <Typography variant="h6" fontWeight={600}>
                {extrato.saldoInicial === null ? '—' : formatCurrency(extrato.saldoInicial)}
              </Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Card variant="outlined"><CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary">Saldo Final</Typography>
              <Typography variant="h6" fontWeight={600} color="primary">
                {extrato.saldoFinal === null ? '—' : formatCurrency(extrato.saldoFinal)}
              </Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Card variant="outlined"><CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary">Total Créditos</Typography>
              <Typography variant="h6" fontWeight={600} color="success.main">{formatCurrency(totais.creditos)}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Card variant="outlined"><CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary">Total Débitos</Typography>
              <Typography variant="h6" fontWeight={600} color="error.main">{formatCurrency(totais.debitos)}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Card variant="outlined"><CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary">Lançamentos</Typography>
              <Typography variant="h6" fontWeight={600}>
                {lancamentos.length}
                <Typography component="span" variant="body2" color={totais.saldo >= 0 ? 'success.main' : 'error.main'} sx={{ ml: 1 }}>
                  ({totais.saldo >= 0 ? '+' : ''}{formatCurrency(totais.saldo)})
                </Typography>
              </Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs: Extrato | BI */}
      {extrato && (
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label={`Extrato (${lancamentos.length})`} icon={<ReceiptIcon />} iconPosition="start" />
            <Tab label="BI / Análise" icon={<BarChartIcon />} iconPosition="start" disabled={!lancamentos.length} />
          </Tabs>
        </Paper>
      )}

      {/* TAB 0: Extrato */}
      {tab === 0 && (
        <Paper variant="outlined">
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Movimentações — {periodoLabel}
            </Typography>
            <TextField size="small" placeholder="Buscar..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ width: 250 }}
            />
          </Box>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Contraparte</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell align="center">Tipo</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  {temSaldoApos && <TableCell align="right">Saldo</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={colCount} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
                ) : !filteredLancamentos.length ? (
                  <TableRow><TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Nenhum lançamento encontrado</Typography>
                  </TableCell></TableRow>
                ) : (
                  filteredLancamentos.map((l, i) => renderLinhaLancamento(l, i, temSaldoApos))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {filteredLancamentos.length > 0 && (
            <Box sx={{ p: 1, textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">
                {filteredLancamentos.length} de {lancamentos.length} lançamentos
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* TAB 1: BI */}
      {tab === 1 && biData && (
        <Stack spacing={3}>
          {/* Evolução mensal */}
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              <DateRange sx={{ mr: 1, verticalAlign: 'middle' }} /> Evolução Mensal
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Mês</TableCell>
                    <TableCell align="right">Créditos</TableCell>
                    <TableCell align="right">Débitos</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                    <TableCell align="center">Lançamentos</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {biData.mesesOrdenados.map(([mes, d]) => (
                    <TableRow key={mes} hover>
                      <TableCell><Typography fontWeight={500}>{mes}</Typography></TableCell>
                      <TableCell align="right"><Typography color="success.main" fontWeight={600}>{formatCurrency(d.creditos)}</Typography></TableCell>
                      <TableCell align="right"><Typography color="error.main" fontWeight={600}>{formatCurrency(d.debitos)}</Typography></TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={600} color={d.creditos - d.debitos >= 0 ? 'success.main' : 'error.main'}>
                          {formatCurrency(d.creditos - d.debitos)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{d.count}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell><Typography fontWeight={700}>TOTAL</Typography></TableCell>
                    <TableCell align="right"><Typography color="success.main" fontWeight={700}>{formatCurrency(totais.creditos)}</Typography></TableCell>
                    <TableCell align="right"><Typography color="error.main" fontWeight={700}>{formatCurrency(totais.debitos)}</Typography></TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={700} color={totais.saldo >= 0 ? 'success.main' : 'error.main'}>
                        {formatCurrency(totais.saldo)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center"><Typography fontWeight={700}>{lancamentos.length}</Typography></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Por Categoria */}
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              <Category sx={{ mr: 1, verticalAlign: 'middle' }} /> Por Categoria
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Categoria</TableCell>
                    <TableCell align="right">Créditos</TableCell>
                    <TableCell align="right">Débitos</TableCell>
                    <TableCell align="right">Líquido</TableCell>
                    <TableCell align="center">Qtd</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {biData.categoriasOrdenadas.map(([cat, d]) => (
                    <TableRow key={cat} hover>
                      <TableCell><Typography fontWeight={500}>{cat}</Typography></TableCell>
                      <TableCell align="right">{d.creditos > 0 ? <Typography color="success.main">{formatCurrency(d.creditos)}</Typography> : '-'}</TableCell>
                      <TableCell align="right">{d.debitos > 0 ? <Typography color="error.main">{formatCurrency(d.debitos)}</Typography> : '-'}</TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={600} color={d.creditos - d.debitos >= 0 ? 'success.main' : 'error.main'}>
                          {formatCurrency(d.creditos - d.debitos)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{d.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Top Pagadores e Destinos */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  <TrendingUp sx={{ mr: 1, verticalAlign: 'middle', color: 'success.main' }} /> Top 20 Pagadores (Créditos)
                </Typography>
                <TableContainer sx={{ maxHeight: 500 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Nome</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="center">Vezes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {biData.topCreditos.map(([nome, d], i) => (
                        <TableRow key={nome} hover sx={{ cursor: 'pointer' }}
                          onClick={() => openDetailModal(nome, 'credito')}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell><Typography variant="body2" fontWeight={500} color="primary">{nome}</Typography></TableCell>
                          <TableCell align="right"><Typography color="success.main" fontWeight={600}>{formatCurrency(d.total)}</Typography></TableCell>
                          <TableCell align="center">{d.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  <TrendingDown sx={{ mr: 1, verticalAlign: 'middle', color: 'error.main' }} /> Top 20 Destinos (Débitos)
                </Typography>
                <TableContainer sx={{ maxHeight: 500 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Nome</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="center">Vezes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {biData.topDebitos.map(([nome, d], i) => (
                        <TableRow key={nome} hover sx={{ cursor: 'pointer' }}
                          onClick={() => openDetailModal(nome, 'debito')}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell><Typography variant="body2" fontWeight={500} color="primary">{nome}</Typography></TableCell>
                          <TableCell align="right"><Typography color="error.main" fontWeight={600}>{formatCurrency(d.total)}</Typography></TableCell>
                          <TableCell align="center">{d.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* Por Dia da Semana */}
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              <DateRange sx={{ mr: 1, verticalAlign: 'middle' }} /> Por Dia da Semana
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Dia</TableCell>
                    <TableCell align="right">Créditos</TableCell>
                    <TableCell align="right">Débitos</TableCell>
                    <TableCell align="center">Lançamentos</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {biData.diaSemanaOrdenado.map(d => (
                    <TableRow key={d.dia} hover>
                      <TableCell><Typography fontWeight={500}>{d.dia}</Typography></TableCell>
                      <TableCell align="right"><Typography color="success.main">{formatCurrency(d.creditos)}</Typography></TableCell>
                      <TableCell align="right"><Typography color="error.main">{formatCurrency(d.debitos)}</Typography></TableCell>
                      <TableCell align="center">{d.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      )}

      {/* Modal de detalhes por contraparte */}
      <Dialog open={detailModal.open} onClose={() => setDetailModal(p => ({ ...p, open: false }))}
        maxWidth="lg" fullWidth PaperProps={{ sx: { maxHeight: '85vh' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {detailModal.nome}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {detailTotais.count} lançamentos — Total: {' '}
              <Typography component="span" fontWeight={700}
                color={detailModal.tipo === 'credito' ? 'success.main' : 'error.main'}>
                {formatCurrency(detailTotais.total)}
              </Typography>
            </Typography>
          </Box>
          <IconButton onClick={() => setDetailModal(p => ({ ...p, open: false }))}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel active={detailSort.field === 'data'} direction={detailSort.field === 'data' ? detailSort.dir : 'desc'}
                      onClick={() => handleDetailSort('data')}>
                      Data
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel active={detailSort.field === 'descricao'} direction={detailSort.field === 'descricao' ? detailSort.dir : 'asc'}
                      onClick={() => handleDetailSort('descricao')}>
                      Descrição
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Documento</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell align="right">
                    <TableSortLabel active={detailSort.field === 'valor'} direction={detailSort.field === 'valor' ? detailSort.dir : 'desc'}
                      onClick={() => handleDetailSort('valor')}>
                      Valor
                    </TableSortLabel>
                  </TableCell>
                  {temSaldoApos && <TableCell align="right">Saldo Após</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {detailLancamentos.map((l, i) => (
                  <TableRow key={l.id || i} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2">{formatDataLancamento(l.data)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{l.descricao}</Typography>
                      {(l.e2eId || l.txid) && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace' }}>
                          {l.e2eId || l.txid}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">{l.contraparteDocumento || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={categoriaDe(l)} />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" fontWeight={600}
                        color={detailModal.tipo === 'credito' ? 'success.main' : 'error.main'}>
                        {detailModal.tipo === 'credito' ? '+' : '-'} {formatCurrency(Number(l.valor))}
                      </Typography>
                    </TableCell>
                    {temSaldoApos && (
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {l.saldoApos === null || l.saldoApos === undefined ? (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        ) : (
                          <Typography variant="body2" fontWeight={500}
                            color={l.saldoApos >= 0 ? 'text.primary' : 'error.main'}>
                            {formatCurrency(l.saldoApos)}
                          </Typography>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {detailTotais.count} registros — Média: {formatCurrency(detailTotais.count > 0 ? detailTotais.total / detailTotais.count : 0)}
          </Typography>
          <Button onClick={() => setDetailModal(p => ({ ...p, open: false }))}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
